import { hydra } from '@/lib/services/Hydra'
import type { HydraErrorType } from '@/lib/services/Hydra/_hydraError'
import type { HydraGetSubscriptionItem } from '@/lib/services/Hydra/account/GET/getSubscriptions'
import type { QuotesCallType } from '@/lib/services/Hydra/quotesCallType'
import type { Alpha2CountryCode } from '@/lib/types/alpha2CountryCode'
import type { CartItemType } from '@/store/types/cart'
import useGlobalStore from '@/store/useGlobalStore'
import { useEffect, useState } from 'react'

type Props = {
  subscription: HydraGetSubscriptionItem | null
  itemToAddToSubscription: CartItemType
}

export const useCalculateNewSubscriptionTotals = ({
  subscription,
  itemToAddToSubscription,
}: Props) => {
  const { user, app } = useGlobalStore()

  const [orderCalculation, setOrderCalculation] = useState<
    (QuotesCallType & HydraErrorType) | null
  >(null)
  const [isLoadingOrderCalculation, setIsLoadingOrderCalculation] =
    useState(false)

  // eslint-disable-next-line sonarjs/cognitive-complexity
  useEffect(() => {
    ;(async () => {
      if (!user || !user.customer || !user.token) return
      if (!subscription) return

      setIsLoadingOrderCalculation(true)

      const calculatedOrder = await hydra.calculateOrder({
        customerType: user.customer.type,
        customerId: user.customer.unicity.toString(),

        items: [
          ...subscription.lines.items.map((line) => ({
            id: line.item.id.unicity || '',
            quantity: line.quantity,
            currency: app.marketExceptions?.currencyCode || 'USD',
            po: 'subscription',
          })),
          {
            id: itemToAddToSubscription.product.sku || '',
            quantity: itemToAddToSubscription.qty,
            currency: app.marketExceptions?.currencyCode || 'USD',
            po: itemToAddToSubscription.po || '',
          },
        ],

        shippingMethod: `type=${subscription.shippingMethod.type}${
          subscription.shippingMethod.location
            ? `&location=${subscription.shippingMethod.location}`
            : ''
        }`,
        customer: user.customer,
        shipToAddress: {
          state: subscription.shipToAddress.state || '',
          country: subscription.shipToAddress.country as Alpha2CountryCode,
          fullName: subscription.shipToName.fullName || '',
          email: subscription.shipToEmail,
          phone: subscription.shipToPhone,
          address1: subscription.shipToAddress.address1,
          address2: subscription.shipToAddress.address2 || '',
          city: subscription.shipToAddress.city,
          zip: subscription.shipToAddress.zip,
        },
        token: user.token,
        market: app.alpha2,
        transactionItem: [],
      })

      if (!calculatedOrder) {
        setIsLoadingOrderCalculation(false)
        setOrderCalculation({ error: { message: 'Error calculating order' } })
        return
      }

      if (calculatedOrder.error) {
        setOrderCalculation(calculatedOrder)
        setIsLoadingOrderCalculation(false)
        return
      }

      setOrderCalculation(calculatedOrder)
      setIsLoadingOrderCalculation(false)
    })()
  }, [subscription])

  return { orderCalculation, isLoadingOrderCalculation }
}
